import React from 'react';
import { observer, inject } from 'mobx-react';

import withUserContext from 'behaviors/withUserContext';
import useCharityProfileById from 'hooks/useCharityProfileById';
import { normalizeContext } from 'util/contextUtils';

import EntityProfile from 'components/EntityProfile/EntityProfile';
import convertFeedToV1 from 'util/feed/convertFeedToV1';
import mergeFeedItems from 'util/feed/mergeFeedItems';
import { CHECKOUT_TYPE, FRAME } from 'stores/CheckoutStoreV2';

const CharityProfileView = ({
  profileStore,
  charityStore,
  authStore,
  uiStore,
  id,
  activeEntity,
  userContext,
}) => {
  useCharityProfileById({ id, userContext });
  const charityData = charityStore.charities.get(+id) || {};

  const feed = charityStore.feeds.get(+id);
  const events = charityStore.events.get(+id);

  const onFollow = () => {
    charityStore.follow({
      follow: !charityData.isSelfFollowing,
      actorContext: normalizeContext(userContext),
      charityId: id,
    });
  };

  const onLike = ({ feedId }) => {
    const feedItem = feed?.find(
      (feedItem) => feedItem?.activityPurchase?.id === feedId,
    );
    const feedItemIndex = feed?.findIndex(
      (feedItem) => feedItem?.activityPurchase?.id === feedId,
    );
    if (feedItem) {
      charityStore.like({
        like: !feedItem.currentEntityLiked,
        id: feedId,
        purchaseId: feedItem.activityPurchase?.id,
        charityId: id,
        userContext: userContext,
      });
    }
    feed[feedItemIndex].likeCount = !feedItem.currentEntityLiked
      ? feedItem.likeCount + 1
      : feedItem.likeCount - 1;
    feed[feedItemIndex].currentEntityLiked = !feedItem.currentEntityLiked;
  };

  const feedLoadMore = () => {
    charityStore.loadMore({ id });
  };

  const getCharitySocials = () => {
    const socials = [
      charityData.facebookName && {
        socialType: 'facebook',
        url: `https://www.facebook.com/${charityData.facebookName}/`,
      },
      charityData.instagramName && {
        socialType: 'instagram',
        url: `https://www.instagram.com/${charityData.instagramName}/`,
      },
      charityData.twitterName && {
        socialType: 'x',
        url: `https://twitter.com/${charityData.twitterName}`,
      },
      charityData.youtubeUrl && {
        socialType: 'youtube',
        url: charityData.youtubeUrl,
      },
    ].filter((s) => s);

    return socials;
  };

  const showCheckoutModal = () => {
    uiStore.openModal('CHECKOUT', {
      giftToken: uiStore.savedUiState?.userToken?.token,
      hideAmount: uiStore.savedUiState?.userToken?.gift?.hideAmount,
      giftAmount: uiStore.savedUiState?.userToken?.gift?.amount,
      email: uiStore.savedUiState.userToken?.email || profileStore?.data?.email,
      initiatorId: uiStore.savedUiState.userToken?.gift?.initiatorCompany?.id,
      checkoutType: uiStore.savedUiState?.userToken?.token
        ? CHECKOUT_TYPE.GIFTED_DONATION
        : CHECKOUT_TYPE.DONATION,
      charityIds: [id],
      eventId: null,
      amount: uiStore.savedUiState?.userToken?.gift?.amount || 500,
      onCheckoutSuccess: async () => {
        profileStore.getProfile();
      },
      frame: uiStore.savedUiState?.userToken?.token
        ? FRAME.GIFT_CHECKOUT
        : FRAME.CHECKOUT,
    });
  };

  return (
    <>
      <EntityProfile
        isCharityProfilePage
        activeEntity={activeEntity}
        onCheckoutSuccess={() => {
          charityStore.getCharityFeed({
            id,
            userContext: normalizeContext(userContext),
          });
        }}
        showCheckoutModal={showCheckoutModal}
        loading={charityStore.loading || !charityStore.charities.has(+id)}
        entityData={{
          ...charityData,
          userSocials: getCharitySocials(),
          profileData: {
            ...charityData,
          },
        }}
        isAuthenticated={authStore.isAuthenticated}
        onFollow={onFollow}
        onLike={onLike}
        uiStore={uiStore}
        events={events}
        eventsLoading={charityStore.eventsLoading}
        feedLoading={charityStore.feedLoading}
        feedLoadMore={feedLoadMore}
        feedLoadingMore={charityStore.feedLoadingMore}
        feed={mergeFeedItems(feed)?.map(convertFeedToV1)}
        entityType="NONPROFIT"
        isVerified
        showEditButton={
          charityData.isSelf || profileStore.activeEntity.sysAdmin
        }
      />
    </>
  );
};

export default withUserContext(
  inject(
    'profileStore',
    'charityStore',
    'authStore',
    'uiStore',
  )(observer(CharityProfileView)),
);
